import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["progressBar", "progressText", "stats", "cancelBtn", "statusText", "feedBody", "feedEmpty"]
  static values = { url: String, interval: { type: Number, default: 3000 } }

  connect() {
    if (this.hasUrlValue && this.urlValue) {
      this.startPolling()
    }
  }

  disconnect() {
    this.stopPolling()
  }

  startPolling() {
    this.poll()
    this.timer = setInterval(() => this.poll(), this.intervalValue)
  }

  stopPolling() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  async poll() {
    try {
      const response = await fetch(this.urlValue)
      if (!response.ok) return

      const data = await response.json()
      this.updateUI(data)

      // Stop polling if not running
      if (data.status !== "running") {
        this.stopPolling()
      }
    } catch (error) {
      console.error("Smart search polling error:", error)
    }
  }

  updateUI(data) {
    // Update progress bar
    if (this.hasProgressBarTarget) {
      const pct = data.total > 0 ? (data.current / data.total) * 100 : 0
      this.progressBarTarget.style.width = `${pct}%`
    }

    // Update progress text
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = data.step || ""
    }

    // Update stats
    if (this.hasStatsTarget) {
      this.statsTarget.innerHTML = `
        <div class="ss-stat"><strong>${data.playlists_matched || 0}</strong> / ${data.target_count} matched</div>
        <div class="ss-stat"><strong>${data.playlists_searched || 0}</strong> checked</div>
        <div class="ss-stat"><strong>${data.playlists_with_ai_match || 0}</strong> AI matches</div>
        <div class="ss-stat"><strong>${data.playlists_no_match || 0}</strong> no match</div>
        ${data.duration ? `<div class="ss-stat">${data.duration} min elapsed</div>` : ""}
      `
    }

    // Update live feed
    if (this.hasFeedBodyTarget && data.recent_playlists && data.recent_playlists.length > 0) {
      if (this.hasFeedEmptyTarget) this.feedEmptyTarget.style.display = "none"
      this.feedBodyTarget.innerHTML = data.recent_playlists.map(p => `
        <tr>
          <td>${this.esc(p.name)}</td>
          <td>${this.esc(p.curator || "\u2014")}</td>
          <td>${p.followers != null ? p.followers.toLocaleString() : "\u2014"}</td>
          <td><span class="gs-score ${this.scoreClass(p.ai_score)}">${p.ai_score != null ? p.ai_score + "%" : "\u2014"}</span></td>
          <td>${this.badge(p.activity)}</td>
          <td>${this.badge(p.bot)}</td>
        </tr>
      `).join("")
    }

    // Update status text and cancel button visibility
    if (data.status === "running") {
      if (this.hasCancelBtnTarget) this.cancelBtnTarget.style.display = "inline-block"
    } else {
      if (this.hasCancelBtnTarget) this.cancelBtnTarget.style.display = "none"

      if (this.hasStatusTextTarget) {
        if (data.status === "complete") {
          this.statusTextTarget.textContent = "Complete!"
          this.statusTextTarget.className = "ss-status-complete"
        } else if (data.status === "cancelled") {
          this.statusTextTarget.textContent = "Cancelled"
          this.statusTextTarget.className = "ss-status-cancelled"
        } else if (data.status === "error") {
          this.statusTextTarget.textContent = "Error"
          this.statusTextTarget.className = "ss-status-error"
        }
      }
    }
  }

  esc(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  scoreClass(score) {
    if (score == null) return ""
    if (score >= 90) return "gs-score-high"
    if (score >= 70) return "gs-score-mid"
    return "gs-score-low"
  }

  badge(status) {
    if (!status) return '<span class="gs-badge gs-badge-pending">pending</span>'
    const cls = status === "active" || status === "real" ? "gs-badge-good"
              : status === "inactive" || status === "bot" ? "gs-badge-bad"
              : "gs-badge-pending"
    return `<span class="gs-badge ${cls}">${this.esc(status)}</span>`
  }
}
